/**
 * Notes Sheet Generator for Excel Exports
 * Creates a standardized Notes sheet with company policies and assumptions
 */

export interface NotesSheetData {
  projectName?: string;
  clientName?: string;
  region?: string;
  creationDate?: Date;
}

/**
 * Generate Notes worksheet data
 */
export function generateNotesSheetData(data?: NotesSheetData): any[][] {
  const sheetData: any[][] = [];

  // Header rows
  sheetData.push(['M & J EVANS CONSTRUCTION LIMITED']);
  sheetData.push(['GENERAL SUMMARY']);
  sheetData.push(['NOTES']);
  sheetData.push([]);

  // Column header
  sheetData.push(['', 'Description']);

  // PAGE 1 NOTES
  const page1Notes = [
    {
      num: '1',
      text: 'Our price is not all risk.',
    },
    {
      num: '2',
      text: 'In the absence a programme of works, our price is fixed until 31/01/2024.  If any specified item increases by greater than 5%, we would look to recover the difference.',
    },
    {
      num: '3',
      text: 'No allowance for carrying out the PC Role.  Our fixed cost and weekly rates are detailed in the R&S BoQ.',
    },
    {
      num: '4',
      text: 'No allowance for construction of compounds, contractors car parking, haul roads, pedestrian walkways or other temporary hardstandings - no details.  No allowance for wheel-wash facility.',
    },
    {
      num: '5',
      text: 'No allowance for the removal or re-erecting of existing boundary fencing or hoarding - no details.  No allowance for temporary fencing to secure the site boundary or to cordon off specific work areas.',
    },
    {
      num: '6',
      text: 'No allowance for works associated with; invasive weeds; protected wildlife species; buried obstructions; breaking out rock; dealing with japanese or dealing with contaminated ground.',
    },
    {
      num: '7',
      text: 'We have assumed that all trees/stumps/rootballs/hedges will be removed by others.  No allowance for tree protection fencing - none shown.',
    },
    {
      num: '8',
      text: 'We have allowed road cleaning of our mess only.',
    },
    {
      num: '9',
      text: 'We have allowed to strip the existing vegetation and leave it to rot down onsite beneath the top soil stockpile.  We have allowed to strip xxxmm thickness of top soil (average of the xx nr trial pits) which will be stockpiled onsite for re-use.  No allowance for testing of this top soil.',
    },
    {
      num: '10',
      text: 'We have / not allowed to remove surplus arisings from site.',
    },
    {
      num: '11',
      text: 'No allowance made for specialist filling works as per NHBC guidance 4.6 for engineered fill, no allowance for zone load testing, risk on material classification, or risk on existing material meeting certain specification criteria (air voids, moisture content etc).  - we have included for standard  earthworks filling and compaction.\n\nFoundations & floors priced as follows:\nTrenchfill foundations as a standard xxxmm from FFL (xxxmm concrete; xxxmm masonry).\nAllowance for additional depths to the formation levels shown on the ………………..  \nNo allowance for mesh to trenchfill foundations where footings span variable strata.\nNo allowance for supply & fix of wind posts/straps.\nPile mat assumed as 300mm thick recycled 6F2.  No allowance for pile setting out nor attendance upon the piling contractor.  Piling assumed as PCC driven hence no allowance for filling of steel piles or disposal of CFA arisings.\nPiled foundations a standard xxxxmm from FFL (50mm blinding; xxxmm reinforced ground beam, xxxmm masonry).\nComment on whether pile cut off levels are provided in order to ascertain any additional masonry heights.\nRebar estimated per linear metre as drawing ……………..  Or as bar schedules …….\nHeave protection ???\nFacings to foundations to be supplied free-issue by xxxxxxxxxxxx or Red Class B\'s supplied by M&J Evans.  If Bellway then potentially free-issue trench block.\nAny DDPC/Secondary DPC shown on Ext Works drawings?  If so:  No allowance for any works associated with the DDPC shown on the drawings.  In the absence of any details, we assume these works will be undertaken by the superstructure bricklayer.\nConcrete beam & polystyrene infill-panel floor (including top sheet insulation) to achieve 0.15 U-value; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres.\nNo allowance for installing proprietary hangers to sub-floor drains.  (NHBC Guidance Jan 2018 indicates that engineered subsoil fill, where required, is a suitable value-engineered solution and we have priced on this basis.)\n\n\n\nNo allowance for land drain to rear gardens - none shown on plot drainage layouts.\n\nDrives are priced as follows:\nWe have allowed for macadam drives to be surfaced with AC6 DBM surface course.  No SMA.\nWe have allowed for 150 x 50mm pcc edgings to drives.  No kerbs.\nNo allowance for recessed covers and frames to block paved drives.\nNo allowance for remedial works prior to surfacing drives.',
    },
  ];

  // PAGE 2 NOTES
  const page2Notes = [
    {
      num: '12',
      text: 'Slabs included as natural grey smooth with 10mm/20mm pea gravel margin.  Slabbing included as shown on the xxxxxxxxxxxx drawings.',
    },
    {
      num: '13',
      text: 'No allowance for shed bases, cycle hoops or fix-only rotary dryers - no details.  Our rates are shown on the relevant BoQ page if required.',
    },
    {
      num: '14',
      text: 'Threshold drain included front doors only.',
    },
    {
      num: '15',
      text: 'We have allowed for 600mm clean cap to landscaped areas (450mm thickness of clean cover and 150mm thickness of top soil).  We have allowed for testing the imported material.  No allowance for membrane or hard-dig layer to the capping - no detail.  We assume that top soil validation and depth testing will be carried out by the client.',
    },
    {
      num: '16',
      text: 'We have allowed to spread and level xxxmm thickness of site-won top soil to the gardens.',
    },
    {
      num: '17',
      text: 'Facings to garden walls to be supplied free-issue by xxxxxxxxxxxx or Red Class B\'s supplied by M & J EVANS CONSTRUCTION LIMITED.',
    },
    {
      num: '18',
      text: 'Retaining walls included complete as per design drawing xxxxxxxxxxxxx.  Scaffold for retaining wall construction to be supplied and installed by others.',
    },
    {
      num: '19',
      text: 'Screen wall foundations included to DPC.',
    },
    {
      num: '20',
      text: 'Garden wall drawings are not site specific therefore no additional depths are priced.',
    },
    {
      num: '21',
      text: 'All railings/fencing/gravel boards to be supplied and installed by others.  No allowance for slab-on-edge in lieu of gravel board nor for concrete backing to gravel boards.',
    },
    {
      num: '22',
      text: 'POS comment here ………….',
    },
    {
      num: '23',
      text: 'POS paths comment here ………….',
    },
    {
      num: '24',
      text: 'No allowance for works associated with the LEAP (play equipment, safety surfacing, street furniture, etc).  We assume that xxxxxxxxxxx will engage a specialist contractor to undertake these works.',
    },
    {
      num: '25',
      text: 'We have included for scaffold platforms at 2000mm wide.  Comment on loading bays (incl or excl) here….',
    },
    {
      num: '26',
      text: 'MDPE water pipe included.  EO for barrier pipe offered as rates only.',
    },
    {
      num: '27',
      text: 'We have assumed that no Virgin Media will be installed on this site.  Please advise?',
    },
    {
      num: '28',
      text: 'No allowance for supply or installation of private streetlighting.  Rate provided for ducting - no details.',
    },
    {
      num: '29',
      text: 'No allowance for any works associated with remote car charging points - no details.',
    },
    {
      num: '30',
      text: 'We have included a schedule of dayworks rates.',
    },
    {
      num: '31',
      text: 'No allowance for the supply of fuel to the xxxxxxxxxxx forklift.',
    },
    {
      num: '32',
      text: 'No allowance for dealing with groundwater.  Should our tender prove competitive we would welcome the opportunity to excavate trial holes onsite to confirm the extent of any groundwater in order to firm up our price.',
    },
  ];

  // Combine all notes
  const notes = [...page1Notes, ...page2Notes];

  // Add each note as a row
  notes.forEach((note) => {
    sheetData.push([note.num, note.text]);
  });

  return sheetData;
}

/**
 * Create Notes worksheet with formatting
 */
export async function createNotesSheet(
  workbook: any,
  logoImageId: any,
  projectInfo: any,
  primaryColor: string
) {
  console.log('📝 Creating Notes sheet...');

  const worksheet = workbook.addWorksheet('Notes');

  // Configure view settings - hide gridlines and zoom out
  worksheet.views = [
    {
      showGridLines: false,
      zoomScale: 85,
      zoomScaleNormal: 85,
    },
  ];

  // Set column widths optimized for portrait printing
  worksheet.columns = [
    { width: 5 },   // Column A - Number
    { width: 80 }, // Column B - Description (adjusted to fit portrait page)
  ];

  let currentRow = 1;

  // Row 1: Logo with merged cells A1:F1 and dark branded background
  if (typeof logoImageId === 'number') {
    try {
      worksheet.addImage(logoImageId, {
        tl: { col: 0.15, row: 0.1 },
        ext: { width: 260, height: 110 },
      });
      worksheet.getRow(1).height = 115;
    } catch (error) {
      console.error('Error adding logo to Notes sheet:', error);
    }
  }
  // Merge row 1 up to column F and apply dark navy background
  try {
    worksheet.mergeCells(1, 1, 1, 2); // A1:F1
    const logoRowCell = worksheet.getCell('A1');
    logoRowCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF0A2642' },
    };
  } catch (error) {
    console.error('Error styling logo row:', error);
  }
  currentRow++;

  // Row 2: Spacing
  worksheet.getRow(currentRow).height = 25;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  currentRow++;

  // Row 3: Sheet title
  const titleCell = worksheet.getCell(`A${currentRow}`);
  titleCell.value = 'NOTES';
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  titleCell.font = { bold: true, size: 14, color: { argb: primaryColor }, name: 'Calibri' };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  // Row 4: Client / Project / Region
  const detailsCell = worksheet.getCell(`A${currentRow}`);
  detailsCell.value = `Client: ${projectInfo.clientName || ''} | Project: ${projectInfo.projectName || ''} | Region: ${projectInfo.region || ''}`;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  detailsCell.font = { size: 10, color: { argb: '666666' }, name: 'Calibri' };
  detailsCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 18;
  currentRow++;

  // Row 5: Generated date
  const displayDate = projectInfo.creationDate instanceof Date
    ? projectInfo.creationDate.toLocaleDateString('en-GB')
    : (projectInfo.creationDate ? new Date(projectInfo.creationDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB'));
  const dateCell = worksheet.getCell(`A${currentRow}`);
  dateCell.value = `Generated: ${displayDate}`;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  dateCell.font = { size: 10, color: { argb: '666666' }, name: 'Calibri' };
  dateCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 18;
  currentRow++;

  // Row 6: Spacer
  worksheet.getRow(currentRow).height = 10;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  currentRow++;

  // Column headers row
  const headerRowNum = currentRow;
  worksheet.getCell(headerRowNum, 1).value = '';
  worksheet.getCell(headerRowNum, 2).value = 'Description';

  for (let col = 1; col <= 2; col++) {
    const cell = worksheet.getCell(headerRowNum, col);
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFF' }, name: 'Calibri' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryColor } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: primaryColor } },
      bottom: { style: 'thin', color: { argb: primaryColor } },
      left: { style: 'thin', color: { argb: primaryColor } },
      right: { style: 'thin', color: { argb: primaryColor } },
    };
  }
  worksheet.getRow(headerRowNum).height = 20;
  currentRow++;

  // PAGE 1 NOTES
  const page1NotesForSheet2 = [
    {
      num: '1',
      text: 'Our price is not all risk.',
    },
    {
      num: '2',
      text: 'In the absence a programme of works, our price is fixed until 31/01/2024.  If any specified item increases by greater than 5%, we would look to recover the difference.',
    },
    {
      num: '3',
      text: 'No allowance for carrying out the PC Role.  Our fixed cost and weekly rates are detailed in the R&S BoQ.',
    },
    {
      num: '4',
      text: 'No allowance for construction of compounds, contractors car parking, haul roads, pedestrian walkways or other temporary hardstandings - no details.  No allowance for wheel-wash facility.',
    },
    {
      num: '5',
      text: 'No allowance for the removal or re-erecting of existing boundary fencing or hoarding - no details.  No allowance for temporary fencing to secure the site boundary or to cordon off specific work areas.',
    },
    {
      num: '6',
      text: 'No allowance for works associated with; invasive weeds; protected wildlife species; buried obstructions; breaking out rock; dealing with japanese or dealing with contaminated ground.',
    },
    {
      num: '7',
      text: 'We have assumed that all trees/stumps/rootballs/hedges will be removed by others.  No allowance for tree protection fencing - none shown.',
    },
    {
      num: '8',
      text: 'We have allowed road cleaning of our mess only.',
    },
    {
      num: '9',
      text: 'We have allowed to strip the existing vegetation and leave it to rot down onsite beneath the top soil stockpile.  We have allowed to strip xxxmm thickness of top soil (average of the xx nr trial pits) which will be stockpiled onsite for re-use.  No allowance for testing of this top soil.',
    },
    {
      num: '10',
      text: 'We have / not allowed to remove surplus arisings from site.',
    },
    {
      num: '11',
      text: 'No allowance made for specialist filling works as per NHBC guidance 4.6 for engineered fill, no allowance for zone load testing, risk on material classification, or risk on existing material meeting certain specification criteria (air voids, moisture content etc).  - we have included for standard  earthworks filling and compaction.\n\nFoundations & floors priced as follows:\nTrenchfill foundations as a standard xxxmm from FFL (xxxmm concrete; xxxmm masonry).\nAllowance for additional depths to the formation levels shown on the ………………..  \nNo allowance for mesh to trenchfill foundations where footings span variable strata.\nNo allowance for supply & fix of wind posts/straps.\nPile mat assumed as 300mm thick recycled 6F2.  No allowance for pile setting out nor attendance upon the piling contractor.  Piling assumed as PCC driven hence no allowance for filling of steel piles or disposal of CFA arisings.\nPiled foundations a standard xxxxmm from FFL (50mm blinding; xxxmm reinforced ground beam, xxxmm masonry).\nComment on whether pile cut off levels are provided in order to ascertain any additional masonry heights.\nRebar estimated per linear metre as drawing ……………..  Or as bar schedules …….\nHeave protection ???\nFacings to foundations to be supplied free-issue by xxxxxxxxxxxx or Red Class B\'s supplied by M&J Evans.  If Bellway then potentially free-issue trench block.\nAny DDPC/Secondary DPC shown on Ext Works drawings?  If so:  No allowance for any works associated with the DDPC shown on the drawings.  In the absence of any details, we assume these works will be undertaken by the superstructure bricklayer.\nConcrete beam & polystyrene infill-panel floor (including top sheet insulation) to achieve 0.15 U-value; 1200g membrane; minimum 250mm void; 75mm concrete screed with Durus macro-fibres.\nNo allowance for installing proprietary hangers to sub-floor drains.  (NHBC Guidance Jan 2018 indicates that engineered subsoil fill, where required, is a suitable value-engineered solution and we have priced on this basis.)\n\n\n\nNo allowance for land drain to rear gardens - none shown on plot drainage layouts.\n\nDrives are priced as follows:\nWe have allowed for macadam drives to be surfaced with AC6 DBM surface course.  No SMA.\nWe have allowed for 150 x 50mm pcc edgings to drives.  No kerbs.\nNo allowance for recessed covers and frames to block paved drives.\nNo allowance for remedial works prior to surfacing drives.',
    },
  ];

  // PAGE 2 NOTES
  const page2NotesForSheet = [
    {
      num: '12',
      text: 'Slabs included as natural grey smooth with 10mm/20mm pea gravel margin.  Slabbing included as shown on the xxxxxxxxxxxx drawings.',
    },
    {
      num: '13',
      text: 'No allowance for shed bases, cycle hoops or fix-only rotary dryers - no details.  Our rates are shown on the relevant BoQ page if required.',
    },
    {
      num: '14',
      text: 'Threshold drain included front doors only.',
    },
    {
      num: '15',
      text: 'We have allowed for 600mm clean cap to landscaped areas (450mm thickness of clean cover and 150mm thickness of top soil).  We have allowed for testing the imported material.  No allowance for membrane or hard-dig layer to the capping - no detail.  We assume that top soil validation and depth testing will be carried out by the client.',
    },
    {
      num: '16',
      text: 'We have allowed to spread and level xxxmm thickness of site-won top soil to the gardens.',
    },
    {
      num: '17',
      text: 'Facings to garden walls to be supplied free-issue by xxxxxxxxxxxx or Red Class B\'s supplied by M & J EVANS CONSTRUCTION LIMITED.',
    },
    {
      num: '18',
      text: 'Retaining walls included complete as per design drawing xxxxxxxxxxxxx.  Scaffold for retaining wall construction to be supplied and installed by others.',
    },
    {
      num: '19',
      text: 'Screen wall foundations included to DPC.',
    },
    {
      num: '20',
      text: 'Garden wall drawings are not site specific therefore no additional depths are priced.',
    },
    {
      num: '21',
      text: 'All railings/fencing/gravel boards to be supplied and installed by others.  No allowance for slab-on-edge in lieu of gravel board nor for concrete backing to gravel boards.',
    },
    {
      num: '22',
      text: 'POS comment here ………….',
    },
    {
      num: '23',
      text: 'POS paths comment here ………….',
    },
    {
      num: '24',
      text: 'No allowance for works associated with the LEAP (play equipment, safety surfacing, street furniture, etc).  We assume that xxxxxxxxxxx will engage a specialist contractor to undertake these works.',
    },
    {
      num: '25',
      text: 'We have included for scaffold platforms at 2000mm wide.  Comment on loading bays (incl or excl) here….',
    },
    {
      num: '26',
      text: 'MDPE water pipe included.  EO for barrier pipe offered as rates only.',
    },
    {
      num: '27',
      text: 'We have assumed that no Virgin Media will be installed on this site.  Please advise?',
    },
    {
      num: '28',
      text: 'No allowance for supply or installation of private streetlighting.  Rate provided for ducting - no details.',
    },
    {
      num: '29',
      text: 'No allowance for any works associated with remote car charging points - no details.',
    },
    {
      num: '30',
      text: 'We have included a schedule of dayworks rates.',
    },
    {
      num: '31',
      text: 'No allowance for the supply of fuel to the xxxxxxxxxxx forklift.',
    },
    {
      num: '32',
      text: 'No allowance for dealing with groundwater.  Should our tender prove competitive we would welcome the opportunity to excavate trial holes onsite to confirm the extent of any groundwater in order to firm up our price.',
    },
  ];

  // Add PAGE 1 notes (1-11)
  page1NotesForSheet2.forEach((note, index) => {
    const numCell = worksheet.getCell(currentRow, 1);
    const textCell = worksheet.getCell(currentRow, 2);

    numCell.value = note.num;
    textCell.value = note.text;

    // Style number cell with table borders
    numCell.font = {
      bold: false,
      size: 9,
      color: { argb: '000000' },
      name: 'Calibri',
    };
    numCell.alignment = { horizontal: 'center', vertical: 'middle' };

    const isLastRowOfPage1 = index === page1NotesForSheet2.length - 1;
    numCell.border = {
      top: { style: 'thin' },
      bottom: isLastRowOfPage1 ? { style: 'medium' } : { style: 'thin' },
      left: { style: 'medium' },
      right: { style: 'thin' },
    };

    // Style text cell with table borders
    textCell.font = {
      size: 9,
      color: { argb: '000000' },
      name: 'Calibri',
    };
    textCell.alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
    };
    textCell.border = {
      top: { style: 'thin' },
      bottom: isLastRowOfPage1 ? { style: 'medium' } : { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'medium' },
    };

    // Adjust row height for wrapped text - ensure full text visibility
    // Column B width is 80 units, accounting for font size 9pt, approximately 75 chars per line
    const lineCount = (note.text.match(/\n/g) || []).length + 1;
    const charsPerLine = 75; // Realistic estimate for 80-width column with font size 9
    const estimatedWrappedLines = Math.ceil(note.text.length / charsPerLine);
    const totalLines = Math.max(lineCount, estimatedWrappedLines);
    const lineHeight = 16; // Height per line in pixels
    const calculatedHeight = totalLines * lineHeight;
    const extraPadding = 5; // Small padding for safety
    worksheet.getRow(currentRow).height = Math.max(22, calculatedHeight + extraPadding);

    currentRow++;
  });

  // Store the row number after page 1 notes for page break
  const pageBreakRow = currentRow - 1;

  // PAGE 2 HEADER - repeat the same logo-style header block
  if (typeof logoImageId === 'number') {
    try {
      worksheet.addImage(logoImageId, {
        tl: { col: 0.15, row: currentRow - 1 + 0.1 },
        ext: { width: 260, height: 110 },
      });
      worksheet.getRow(currentRow).height = 115;
    } catch (error) {
      console.error('Error adding logo to Notes sheet page 2:', error);
    }
  }
  // Merge row up to column B and apply dark navy background (same as page 1)
  try {
    worksheet.mergeCells(currentRow, 1, currentRow, 2); // A:B
    const p2LogoCell = worksheet.getCell(currentRow, 1);
    p2LogoCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF0A2642' },
    };
  } catch (error) {
    console.error('Error styling page 2 logo row:', error);
  }
  currentRow++;

  worksheet.getRow(currentRow).height = 25;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  currentRow++;

  const p2TitleCell = worksheet.getCell(`A${currentRow}`);
  p2TitleCell.value = 'NOTES';
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  p2TitleCell.font = { bold: true, size: 14, color: { argb: primaryColor }, name: 'Calibri' };
  p2TitleCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 22;
  currentRow++;

  const p2DetailsCell = worksheet.getCell(`A${currentRow}`);
  p2DetailsCell.value = `Client: ${projectInfo.clientName || ''} | Project: ${projectInfo.projectName || ''} | Region: ${projectInfo.region || ''}`;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  p2DetailsCell.font = { size: 10, color: { argb: '666666' }, name: 'Calibri' };
  p2DetailsCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 18;
  currentRow++;

  const p2DateCell = worksheet.getCell(`A${currentRow}`);
  p2DateCell.value = `Generated: ${displayDate}`;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  p2DateCell.font = { size: 10, color: { argb: '666666' }, name: 'Calibri' };
  p2DateCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
  worksheet.getRow(currentRow).height = 18;
  currentRow++;

  worksheet.getRow(currentRow).height = 10;
  worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
  currentRow++;

  // Column headers row for page 2
  const page2HeaderRowNum = currentRow;
  worksheet.getCell(page2HeaderRowNum, 1).value = '';
  worksheet.getCell(page2HeaderRowNum, 2).value = 'Description';

  for (let col = 1; col <= 2; col++) {
    const cell = worksheet.getCell(page2HeaderRowNum, col);
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFF' }, name: 'Calibri' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryColor } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: primaryColor } },
      bottom: { style: 'thin', color: { argb: primaryColor } },
      left: { style: 'thin', color: { argb: primaryColor } },
      right: { style: 'thin', color: { argb: primaryColor } },
    };
  }
  worksheet.getRow(page2HeaderRowNum).height = 20;
  currentRow++;

  // Add PAGE 2 notes (12-32)
  page2NotesForSheet.forEach((note, index) => {
    const numCell = worksheet.getCell(currentRow, 1);
    const textCell = worksheet.getCell(currentRow, 2);

    numCell.value = note.num;
    textCell.value = note.text;

    // Style number cell with table borders
    numCell.font = {
      bold: false,
      size: 9,
      color: { argb: '000000' },
      name: 'Calibri',
    };
    numCell.alignment = { horizontal: 'center', vertical: 'middle' };

    const isLastRowOfPage2 = index === page2NotesForSheet.length - 1;
    numCell.border = {
      top: { style: 'thin' },
      bottom: isLastRowOfPage2 ? { style: 'medium' } : { style: 'thin' },
      left: { style: 'medium' },
      right: { style: 'thin' },
    };

    // Style text cell with table borders
    textCell.font = {
      size: 9,
      color: { argb: '000000' },
      name: 'Calibri',
    };
    textCell.alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
    };
    textCell.border = {
      top: { style: 'thin' },
      bottom: isLastRowOfPage2 ? { style: 'medium' } : { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'medium' },
    };

    // Adjust row height for wrapped text - ensure full text visibility
    // Column B width is 80 units, accounting for font size 9pt, approximately 75 chars per line
    const lineCount = (note.text.match(/\n/g) || []).length + 1;
    const charsPerLine = 75; // Realistic estimate for 80-width column with font size 9
    const estimatedWrappedLines = Math.ceil(note.text.length / charsPerLine);
    const totalLines = Math.max(lineCount, estimatedWrappedLines);
    const lineHeight = 16; // Height per line in pixels
    const calculatedHeight = totalLines * lineHeight;
    const extraPadding = 5; // Small padding for safety
    worksheet.getRow(currentRow).height = Math.max(22, calculatedHeight + extraPadding);

    currentRow++;
  });

  // Set explicit page break after item 11 (page 1)
  // ExcelJS uses rowBreaks array to define where page breaks occur
  worksheet.rowBreaks = [{ id: pageBreakRow, min: 0, max: 16383, man: true }];

  // Set page setup for printing - portrait mode
  // Do NOT use fitToHeight - let the manual page break control pagination
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1, // Fit all columns on one page width
    fitToHeight: 0, // Do NOT scale height - use natural page breaks
    scale: 100, // Use 100% scaling
    margins: {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3
    },
    horizontalCentered: false,
    verticalCentered: false,
    printArea: `A1:B${currentRow - 1}`, // Set print area to include all data
  };

  console.log('✅ Notes sheet created successfully with:');
  console.log('   - Page break at row:', pageBreakRow);
  console.log('   - Total rows:', currentRow - 1);
  console.log('   - Page 1: Items 1-11');
  console.log('   - Page 2: Items 12-32 (with headers repeated)');
}
